import OpenInFullOutlined from '@mui/icons-material/OpenInFullOutlined'
import { Box, IconButton } from '@mui/material'
import type { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import ChartSection from '@/features/bidding/components/cascading/bid-ladder/shared/ChartSection'
import ReviewBidsButton from '@/features/bidding/components/cascading/bid-ladder/shared/ReviewBidsButton'
import SectionHeader from '@/features/bidding/components/cascading/bid-ladder/shared/SectionHeader'
import { useResponsiveChartHeight } from '@/features/bidding/components/cascading/bid-ladder/shared/useResponsiveChartHeight'
import BidLadderInputSymmetrical from '@/features/bidding/components/cascading/bid-ladder/symmetrical/BidLadderInputSymmetrical'
import BidLadderChartsSymmetrical from '@/features/bidding/components/cascading/charts/BidLadderChartsSymmetrical'
import ExpandChartDialog from '@/features/bidding/components/cascading/charts/ExpandChartDialog'
import HourlyPriceChart from '@/features/bidding/components/cascading/charts/HourlyPriceChart'
import HourlyVolumeChart from '@/features/bidding/components/cascading/charts/HourlyVolumeChart'
import { CASCADE_MARKETS } from '@/features/bidding/constants'
import type { UseBidLadderInputStateSymmetricalReturn } from '@/features/bidding/hooks/useBidLadderInputStateSymmetrical'
import type { UsePricingStrategyStateReturn } from '@/features/bidding/hooks/usePricingStrategyState'
import type { AggregateAvailabilityResponse } from '@/features/bidding/types/aggregateAvailability'
import type { AggregateSuggestedBidResponse } from '@/features/bidding/types/aggregateSuggestedBid'
import type { DragPayloadSymmetrical } from '@/features/bidding/types/bidLadderInputSymmetrical'
import type { CascadeMarketId } from '@/features/bidding/types/market'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import { cascadeMarketToForecastMarket } from '@/features/bidding/utils/forecast/marketProgramNames'
import { getMarketVolumeGranularity } from '@/features/bidding/utils/marketCapabilities'

type BidLadderContentSymmetricalProps = {
  bidLadderState: UseBidLadderInputStateSymmetricalReturn
  forecastsData: FormattedRow[] | undefined
  selectedDeliveryDay: DateTime
  totalMWAvailable: number
  deliveryDay: MarketDate
  selectedMarket: CascadeMarketId
  /** Aggregate availability to show as overlay lines on the price chart */
  aggregateAvailability?: AggregateAvailabilityResponse | null
  /** Aggregate suggested bids to show as overlay lines on the volume chart */
  aggregateSuggestedBids?: AggregateSuggestedBidResponse | null
  pricingStrategy?: UsePricingStrategyStateReturn
  /** Whether drag and drop is enabled for this market (defaults to true for mFRR) */
  isDragDropEnabled?: boolean
  onDragStartFromLadder?: (payload: DragPayloadSymmetrical) => void
  onDragEndFromLadder?: () => void
  onOpenPricingConfiguration?: () => void
  /** Number of activation groups available (used by ReviewBidsButton to determine disabled state) */
  activationGroupsCount: number
  onReviewBids?: () => void
}

function BidLadderContentSymmetrical({
  bidLadderState,
  forecastsData,
  selectedDeliveryDay,
  totalMWAvailable,
  deliveryDay,
  selectedMarket,
  aggregateAvailability,
  aggregateSuggestedBids,
  pricingStrategy,
  isDragDropEnabled = true,
  onDragStartFromLadder,
  onDragEndFromLadder,
  onOpenPricingConfiguration,
  activationGroupsCount,
  onReviewBids,
}: Readonly<BidLadderContentSymmetricalProps>) {
  const { t } = useTranslation()
  const { overviewChartHeight } = useResponsiveChartHeight()
  const [expandedChart, setExpandedChart] = useState<'price' | 'overview' | 'volume' | null>(null)

  // Format delivery date for display
  const formattedDeliveryDate = deliveryDay.getStartOfDay().toFormat('EEE, MMM d')
  const marketLabel = CASCADE_MARKETS.find((m) => m.id === selectedMarket)?.label ?? selectedMarket
  const fixedModeDecimalPlaces = getMarketVolumeGranularity(selectedMarket).decimalPlaces

  // Count assigned activation groups
  const assignedGroupsCount = useMemo(() => {
    let count = 0
    for (const level of bidLadderState.levels) {
      count += level.assignedGroups.length
    }
    return count
  }, [bidLadderState.levels])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'grey.50',
      }}
    >
      {/* Scrollable charts area */}
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          overflow: 'auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {/* Hourly Price Section */}
        <ChartSection
          action={
            <IconButton size="small" onClick={() => setExpandedChart('price')}>
              <OpenInFullOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          }
          minHeight={280}
          subtitle={`${formattedDeliveryDate} · ${t('bidding.bidding_cascading.bid_ladder.hourly_price_subtitle')}`}
          title={t('bidding.bidding_cascading.bid_ladder.hourly_price_title')}
        >
          <HourlyPriceChart
            bidLevelInputs={bidLadderState.levels}
            forecastsData={forecastsData}
            market={cascadeMarketToForecastMarket(selectedMarket)}
            pricingConfig={pricingStrategy?.config}
            selectedDeliveryDay={selectedDeliveryDay}
          />
        </ChartSection>

        {/* Average Bid Curve Section */}
        <ChartSection
          action={
            <IconButton size="small" onClick={() => setExpandedChart('overview')}>
              <OpenInFullOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          }
          contentBackgroundColor="grey.50"
          minHeight={overviewChartHeight}
          subtitle={`${formattedDeliveryDate} · ${t('bidding.bidding_cascading.bid_ladder.preview_subtitle')}`}
          title={t('bidding.bidding_cascading.bid_ladder.preview_title')}
        >
          <BidLadderChartsSymmetrical
            aggregateAvailability={aggregateAvailability}
            aggregateSuggestedBids={aggregateSuggestedBids}
            bidLevelInputs={bidLadderState.levels}
            forecastsData={forecastsData}
            height={overviewChartHeight}
            market={cascadeMarketToForecastMarket(selectedMarket)}
            selectedDeliveryDay={selectedDeliveryDay}
            totalMWAvailable={totalMWAvailable}
          />
        </ChartSection>

        {/* Hourly Volume Section */}
        <ChartSection
          action={
            <IconButton size="small" onClick={() => setExpandedChart('volume')}>
              <OpenInFullOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          }
          minHeight={250}
          showBorderBottom={false}
          subtitle={`${formattedDeliveryDate} · ${t('bidding.bidding_cascading.bid_ladder.hourly_volume_subtitle')}`}
          title={t('bidding.bidding_cascading.bid_ladder.hourly_volume_title', { market: marketLabel })}
        >
          <HourlyVolumeChart
            aggregateAvailability={aggregateAvailability}
            aggregateSuggestedBids={aggregateSuggestedBids}
            bidLevelInputs={bidLadderState.levels}
            market={cascadeMarketToForecastMarket(selectedMarket)}
            selectedDeliveryDay={selectedDeliveryDay}
          />
        </ChartSection>
      </Box>

      {/* Fixed bottom: Bid Levels Input + Actions */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          position: 'relative',
          zIndex: 1,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            backgroundColor: 'divider',
            boxShadow: '0 -1px 3px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        {/* Bid Levels Input Section */}
        <SectionHeader
          subtitle={`${bidLadderState.levels.length} levels · ${assignedGroupsCount} groups`}
          title={t('bidding.bidding_cascading.bid_ladder.title')}
        />
        <Box
          sx={{
            backgroundColor: 'background.paper',
          }}
        >
          <BidLadderInputSymmetrical
            assignGroups={bidLadderState.assignGroups}
            fixedModeDecimalPlaces={fixedModeDecimalPlaces}
            getTotalMWAcrossAllLevels={bidLadderState.getTotalMWAcrossAllLevels}
            isDragDropEnabled={isDragDropEnabled}
            levels={bidLadderState.levels}
            pricingStrategy={onOpenPricingConfiguration ? pricingStrategy : undefined}
            unassignRegulationObjectGroup={bidLadderState.unassignRegulationObjectGroup}
            updatePrice={bidLadderState.updatePrice}
            updateVolumeCapacity={bidLadderState.updateVolumeCapacity}
            onDragEndFromLadder={isDragDropEnabled ? onDragEndFromLadder : undefined}
            onDragStartFromLadder={isDragDropEnabled ? onDragStartFromLadder : undefined}
            onOpenPricingConfiguration={onOpenPricingConfiguration}
          />
        </Box>

        {/* Review Bids Button */}
        <ReviewBidsButton
          activationGroupsCount={activationGroupsCount}
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          levels={bidLadderState.levels}
          selectedMarket={selectedMarket}
          onReviewBids={onReviewBids}
        />
      </Box>

      {/* Expanded Chart Dialogs */}
      <ExpandChartDialog
        open={expandedChart === 'price'}
        title={t('bidding.bidding_cascading.bid_ladder.hourly_price_title')}
        onClose={() => setExpandedChart(null)}
      >
        <HourlyPriceChart
          bidLevelInputs={bidLadderState.levels}
          forecastsData={forecastsData}
          height={600}
          market={cascadeMarketToForecastMarket(selectedMarket)}
          pricingConfig={pricingStrategy?.config}
          selectedDeliveryDay={selectedDeliveryDay}
        />
      </ExpandChartDialog>

      <ExpandChartDialog
        open={expandedChart === 'overview'}
        title={t('bidding.bidding_cascading.bid_ladder.preview_title')}
        onClose={() => setExpandedChart(null)}
      >
        <BidLadderChartsSymmetrical
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          bidLevelInputs={bidLadderState.levels}
          forecastsData={forecastsData}
          height={600}
          market={cascadeMarketToForecastMarket(selectedMarket)}
          selectedDeliveryDay={selectedDeliveryDay}
          totalMWAvailable={totalMWAvailable}
        />
      </ExpandChartDialog>

      <ExpandChartDialog
        open={expandedChart === 'volume'}
        title={t('bidding.bidding_cascading.bid_ladder.hourly_volume_title', { market: marketLabel })}
        onClose={() => setExpandedChart(null)}
      >
        <HourlyVolumeChart
          aggregateAvailability={aggregateAvailability}
          aggregateSuggestedBids={aggregateSuggestedBids}
          bidLevelInputs={bidLadderState.levels}
          height={600}
          market={cascadeMarketToForecastMarket(selectedMarket)}
          selectedDeliveryDay={selectedDeliveryDay}
        />
      </ExpandChartDialog>
    </Box>
  )
}

export default BidLadderContentSymmetrical
